import { v4 as uuidv4 } from "uuid";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { createCardBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers";
import { createImageBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/mediaBlockHelpers";
import {
  createButtonBlock,
  createH2Block,
  createPBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBackgroundColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getBorderRadiusOptions } from "@circle-react-shared/BuilderUI/utils/imgStyleHelpers";
import { getBackgroundImageOptions } from "../../../utils/backgroundHelpers";
import { getSizingOptions } from "../../../utils/sizeStyleHelpers";
import bookImagePlaceholder from "../../CallToAction/CardCallToAction/book_image_placeholder.png";
import calendarImagePlaceholder from "../../CallToAction/CardCallToAction/calendar_image_placeholder.png";
import supportImagePlaceholder from "../../CallToAction/CardCallToAction/support_image_placeholder.png";
import usersImagePlaceholder from "../../CallToAction/CardCallToAction/users_image_placeholder.png";
import { secondaryBlockDefaultStyles } from "../constants";
import placeholderImage from "./homepage_block_6_default_image.png";

const cards = [
  {
    image: {
      signedId: "",
      placeholder: usersImagePlaceholder,
    },
    h5Text: "Mastermind Sessions",
    pText:
      "Connect in small groups to discuss challenges & solutions with peers who understand your leadership journey. Our members report solving problems in hours that would have taken months on their own.",
  },
  {
    image: {
      signedId: "",
      placeholder: calendarImagePlaceholder,
    },
    h5Text: "Live Coaching & Workshops",
    pText:
      "Monthly deep dives on leadership strategies with expert facilitation and interactive learning experiences. These aren't passive webinars - they're transformative sessions that drive immediate action.",
  },
  {
    image: {
      signedId: "",
      placeholder: supportImagePlaceholder,
    },
    h5Text: "Supportive Peer Network",
    pText:
      "Ongoing accountability and shared learning with leaders who are committed to growth and excellence. Unlike social media groups, our community is carefully curated for quality conversations and real support.",
  },
  {
    image: {
      signedId: "",
      placeholder: bookImagePlaceholder,
    },
    h5Text: "Exclusive Content & Insights",
    pText:
      "Early access to leadership frameworks, guest experts, and case studies to accelerate your development. Members get resources worth thousands of dollars that aren't available anywhere else.",
  },
] as const;

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "HomepageBlock6",
  props: {
    header: {
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: "A Leadership Community Designed for Growth",
        }),
        createPBlock({
          text: "Breakthroughs happen faster when you have the right people around you. Join an exclusive network of leaders, gain insights, and elevate your leadership journey together.",
        }),
      ],
    },
    content: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          placeholder: placeholderImage,
          styles: {
            borderRadius: getBorderRadiusOptions({
              defaultValue: {
                border_left_top_radius: 20,
                border_left_bottom_radius: 20,
                border_right_top_radius: 20,
                border_right_bottom_radius: 20,
              },
            }),
            sizing: getSizingOptions({
              defaultValue: {
                maxHeight: "none",
                maxWidth: "none",
                width: "100%",
                height: "100%",
                minHeight: "auto",
                minWidth: "auto",
              },
            }),
          },
        }),
      ],
    },
    cards: {
      prop: "children",
      type: "children",
      value: cards.map(card => createCardBlock(card)),
    },
    footer: {
      prop: "children",
      type: "children",
      value: [
        createButtonBlock({
          text: "Start Connecting with Like-Minded Leaders Today",
          href: "#",
        }),
        createPBlock({
          text: "Limited membership spots available this quarter",
          paragraphStyle: "p3",
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        backgroundColor: getBackgroundColorOptions({
          defaultValue: secondaryBlockDefaultStyles.backgroundColor,
        }),
        backgroundImage: getBackgroundImageOptions(),
        padding: getPaddingOptions({
          defaultValue: secondaryBlockDefaultStyles.padding,
        }),
        margin: getMarginOptions({
          defaultValue: secondaryBlockDefaultStyles.margin,
        }),
      },
    },
  },
};
